import { Action } from "./actions";
import {
  clampMobileJoystickOffset,
  mobileJoystickDirections,
} from "./mobile-control-rules";
import { sharedInput } from "./input-controller";

const JOYSTICK_RADIUS = 56;

function isTouchUi(): boolean {
  return (
    navigator.maxTouchPoints > 0 ||
    window.matchMedia("(pointer: coarse)").matches ||
    "ontouchstart" in window
  );
}

/**
 * DOM virtual stick + buttons → shared InputController (Kerning mobile controls 축약).
 */
export function setupMobileControls(): void {
  if (!isTouchUi()) return;
  if (document.getElementById("mobile-controls")) return;

  document.documentElement.dataset.inputMode = "touch";

  const root = document.createElement("div");
  root.id = "mobile-controls";
  root.innerHTML = `
    <div class="mc-stick" id="mc-stick" aria-label="move">
      <div class="mc-stick-knob" id="mc-stick-knob"></div>
    </div>
    <div class="mc-actions">
      <button type="button" class="mc-btn" data-action="jump">Jump</button>
      <button type="button" class="mc-btn mc-btn-primary" data-action="confirm">Hit</button>
    </div>
  `;
  document.body.appendChild(root);

  const stick = root.querySelector<HTMLElement>("#mc-stick")!;
  const knob = root.querySelector<HTMLElement>("#mc-stick-knob")!;
  let activePointer: number | null = null;

  const resetStick = (): void => {
    activePointer = null;
    knob.style.transform = "translate(0px, 0px)";
    sharedInput.setVirtual(Action.Left, false);
    sharedInput.setVirtual(Action.Right, false);
  };

  const onStickMove = (clientX: number, clientY: number): void => {
    const rect = stick.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const rawX = clientX - cx;
    const rawY = clientY - cy;
    const x = clampMobileJoystickOffset(rawX, JOYSTICK_RADIUS);
    // vertical ignored for move; keep knob visually constrained
    const y = Math.max(-JOYSTICK_RADIUS, Math.min(JOYSTICK_RADIUS, rawY)) * 0.35;
    knob.style.transform = `translate(${x}px, ${y}px)`;
    const dir = mobileJoystickDirections(x, JOYSTICK_RADIUS);
    sharedInput.setVirtual(Action.Left, dir.left);
    sharedInput.setVirtual(Action.Right, dir.right);
  };

  stick.addEventListener(
    "pointerdown",
    (event) => {
      event.preventDefault();
      stick.setPointerCapture(event.pointerId);
      activePointer = event.pointerId;
      onStickMove(event.clientX, event.clientY);
    },
    { passive: false },
  );
  stick.addEventListener("pointermove", (event) => {
    if (activePointer !== event.pointerId) return;
    onStickMove(event.clientX, event.clientY);
  });
  stick.addEventListener("pointerup", (event) => {
    if (activePointer !== event.pointerId) return;
    resetStick();
  });
  stick.addEventListener("pointercancel", resetStick);

  root.querySelectorAll<HTMLButtonElement>(".mc-btn").forEach((btn) => {
    const actionName = btn.dataset.action;
    const action =
      actionName === "jump"
        ? Action.Jump
        : actionName === "confirm"
          ? Action.Confirm
          : null;
    if (!action) return;

    const down = (event: PointerEvent): void => {
      event.preventDefault();
      btn.setPointerCapture(event.pointerId);
      sharedInput.setVirtual(action, true);
    };
    const up = (): void => {
      sharedInput.setVirtual(action, false);
    };
    btn.addEventListener("pointerdown", down, { passive: false });
    btn.addEventListener("pointerup", up);
    btn.addEventListener("pointercancel", up);
  });
}
